import {
  GetDetailsResponseType,
  transformAppDetails,
} from 'QuoteApp/services/endpoints/GetDetails';
import {
  Disclosures,
  GetDisclosuresResponseType,
  ServerResponseType,
  transformDisclosuresResponse,
  transformDisclosuresResponseWithConfig,
} from 'QuoteApp/services/endpoints/GetDisclosures';
import { useLazyQuoteApi } from 'Shared/services/Api';
import { useCallback, useEffect, useState } from 'react';

export const useAgentConfirmation = (
  appId: string,
  agentSignature2FeatureFlag: boolean
) => {
  const [
    agentSignatureAffirmation,
    setAgentSignatureAffirmation,
  ] = useState<GetDisclosuresResponseType | null>(null);
  const [
    agentBindAuthorization,
    setAgentBindAuthorization,
  ] = useState<GetDisclosuresResponseType | null>(null);
  const [agentTOS, setAgentTOS] = useState<GetDisclosuresResponseType | null>(
    null
  );
  const [
    vehiclePhotos,
    setVehiclePhotos,
  ] = useState<GetDisclosuresResponseType | null>(null);

  const disclosureTransformer = useCallback(
    (res: ServerResponseType) =>
      transformDisclosuresResponseWithConfig(res, {
        // need target "_blank" to not be stripped
        ADD_ATTR: ['target'],
      }),
    []
  );

  const [
    {
      loading: agentSignatureAffirmationDataLoading,
      error: agentSignatureAffirmationError,
    },
    fetchAgentSignatureAffirmationData,
  ] = useLazyQuoteApi<GetDisclosuresResponseType>({
    url: `insurance_applications/${appId}/document_signatures/${Disclosures.AgentSignatureAffirmation}`,
    transformResponse: transformDisclosuresResponse,
  });
  const [
    {
      loading: agentBindAuthorizationDataLoading,
      error: agentBindAuthorizationError,
    },
    fetchAgentBindAuthorizationData,
  ] = useLazyQuoteApi<GetDisclosuresResponseType>({
    url: `insurance_applications/${appId}/document_signatures/${Disclosures.AgentBindAuthorization}`,
    transformResponse: transformDisclosuresResponse,
  });
  const [
    { loading: agentTOSDataLoading, error: agentTOSError },
    fetchAgentTOSData,
  ] = useLazyQuoteApi<GetDisclosuresResponseType>({
    url: `insurance_applications/${appId}/document_signatures/${Disclosures.AgentTOS}`,
    transformResponse: disclosureTransformer,
  });
  const [
    { loading: vehiclePhotosDataLoading, error: vehiclePhotosError },
    fetchVehiclePhotosData,
  ] = useLazyQuoteApi<GetDisclosuresResponseType>({
    url: `/insurance_applications/${appId}/document_signatures/${Disclosures.VehiclePhotos}`,
  });

  const [{ error: postDisclosurerError }, postDisclosure] = useLazyQuoteApi<
    GetDetailsResponseType
  >({
    method: 'POST',
    transformResponse: transformAppDetails,
  });

  useEffect(() => {
    async function fetchData() {
      if (agentSignature2FeatureFlag) {
        if (
          !agentSignatureAffirmation &&
          !agentSignatureAffirmationDataLoading
        ) {
          const { data } = await fetchAgentSignatureAffirmationData();
          setAgentSignatureAffirmation(data);
        }

        if (!agentBindAuthorization && !agentBindAuthorizationDataLoading) {
          const { data } = await fetchAgentBindAuthorizationData();
          setAgentBindAuthorization(data);
        }

        if (!agentTOS && !agentTOSDataLoading) {
          const { data } = await fetchAgentTOSData();
          setAgentTOS(data);
        }

        if (!vehiclePhotosDataLoading && !vehiclePhotos) {
          const { data } = await fetchVehiclePhotosData();
          setVehiclePhotos(data);
        }
      }
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    agentSignatureAffirmation,
    agentSignatureAffirmationDataLoading,
    agentBindAuthorization,
    agentBindAuthorizationDataLoading,
    agentTOS,
    agentTOSDataLoading,
    vehiclePhotosDataLoading,
    vehiclePhotos,
  ]);

  useEffect(() => {
    if (
      agentSignatureAffirmationError &&
      agentBindAuthorizationError &&
      agentTOSError &&
      vehiclePhotosError &&
      !agentSignature2FeatureFlag
    ) {
      // not need to handle error if agentSignature2FeatureFlag is off
      return;
    } else if (
      agentSignatureAffirmationError &&
      agentBindAuthorizationError &&
      agentTOSError &&
      vehiclePhotosError &&
      agentSignature2FeatureFlag
    ) {
      throw agentSignatureAffirmationError ||
        agentBindAuthorizationError ||
        agentTOSError ||
        vehiclePhotosError;
    }
  }, [
    agentSignatureAffirmationError,
    agentBindAuthorizationError,
    agentTOSError,
    vehiclePhotosError,
    agentSignature2FeatureFlag,
  ]);

  const postConfirmation = async (): Promise<GetDetailsResponseType> => {
    try {
      await postDisclosure({
        url: `/insurance_applications/${appId}/document_signatures/${Disclosures.AgentSignatureAffirmation}/complete`,
      });
      await postDisclosure({
        url: `/insurance_applications/${appId}/document_signatures/${Disclosures.AgentBindAuthorization}/complete`,
      });

      const completedConfimation = await postDisclosure({
        url: `/insurance_applications/${appId}/document_signatures/${Disclosures.AgentTOS}/complete`,
      });

      return completedConfimation.data;
    } catch {
      throw new Error(postDisclosurerError?.message);
    }
  };

  return {
    agentSignatureAffirmation,
    agentSignatureAffirmationDataLoading,
    agentBindAuthorization,
    agentBindAuthorizationDataLoading,
    agentTOSDataLoading,
    agentTOS,
    vehiclePhotos,
    vehiclePhotosDataLoading,
    postConfirmation,
  };
};
